/**
 * Phase 3 Integration Test Setup Utility
 *
 * Functions to rapidly create a testable game state for integration testing.
 * All functions use the actual Supabase database (not mocks).
 */

import { supabase } from '@/lib/supabase'
import { generateCohortsForDistrict } from '@/lib/cohortGenerator'
import { generateCandidate } from '@/lib/candidateGenerator'
import type {
  IdeologyVector,
  RecruitmentSource,
  ActionType,
  TargetType,
} from '@/types'

// ============================================================================
// Types
// ============================================================================

export interface TestWorldData {
  worldId: string
  nationId: string
  regions: { id: string; name: string }[]
  districts: { id: string; name: string; regionId: string; population: number }[]
}

export interface TestPartyData {
  id: string
  name: string
  abbreviation: string
  color: string
  ideology: IdeologyVector
}

export interface TestCandidateData {
  id: string
  name: string
  partyId: string
  role: string
}

export interface TestSetupResult {
  world: TestWorldData
  parties: TestPartyData[]
  candidates: TestCandidateData[]
  electionId: string | null
  cohortCount: number
}

export interface TestCleanupResult {
  deletedWorlds: number
  errors: string[]
}

// ============================================================================
// Party Ideology Presets
// ============================================================================

const PARTY_PRESETS = {
  leftProgressive: {
    name: 'Social Progress Party',
    abbreviation: 'SPP',
    color: '#E53E3E',
    ideology: [70, 60, 40, 50, 60, 30, 40] as IdeologyVector, // high regulation, progressive, moderate civil lib
  },
  rightConservative: {
    name: 'National Liberty Party',
    abbreviation: 'NLP',
    color: '#3182CE',
    ideology: [-60, -50, -20, -40, -50, 20, 30] as IdeologyVector, // low regulation, traditional
  },
  centrist: {
    name: 'Democratic Center',
    abbreviation: 'DC',
    color: '#D69E2E',
    ideology: [10, 5, 15, 10, 5, 0, 10] as IdeologyVector, // moderate on all axes
  },
  greenNiche: {
    name: 'Green Future Alliance',
    abbreviation: 'GFA',
    color: '#38A169',
    ideology: [30, 40, 50, 80, 30, -20, -10] as IdeologyVector, // extreme environment
  },
  farRight: {
    name: 'Patriot Front',
    abbreviation: 'PF',
    color: '#805AD5',
    ideology: [-80, -70, -60, -30, -80, 70, 60] as IdeologyVector, // extreme right, nationalist
  },
  socialist: {
    name: 'Workers United',
    abbreviation: 'WU',
    color: '#C53030',
    ideology: [90, 70, 30, 40, 50, 50, 20] as IdeologyVector, // extreme economic left
  },
} as const

type PartyPresetKey = keyof typeof PARTY_PRESETS

// ============================================================================
// Test World Creation
// ============================================================================

/**
 * Creates a complete test world: 1 world, 1 nation, 2 regions, 4 districts
 */
export async function createTestWorld(
  suffix: string = ''
): Promise<TestWorldData> {
  const label = suffix ? ` ${suffix}` : ''

  // Create world
  const { data: world, error: worldError } = await supabase
    .from('worlds')
    .insert({
      name: `Test World${label}`,
      tick_interval_hours: 1,
      current_tick: 0,
      status: 'active',
      settings: { max_ticks: 100, first_election_tick: 16 },
    })
    .select()
    .single()

  if (worldError) throw new Error(`Failed to create world: ${worldError.message}`)

  // Create nation
  const { data: nation, error: nationError } = await supabase
    .from('nations')
    .insert({
      world_id: world.id,
      name: `Test Nation${label}`,
      government_type: 'parliamentary',
      electoral_system: 'nationwide_pr',
      term_length_ticks: 16,
      threshold_percent: 5,
      max_parties: 8,
      next_election_tick: 16,
    })
    .select()
    .single()

  if (nationError) throw new Error(`Failed to create nation: ${nationError.message}`)

  // Create 2 regions
  const regionDefs = [
    { name: `Northern Region${label}`, region_type: 'urban_core', seat_count: 5, population: 500000 },
    { name: `Southern Region${label}`, region_type: 'coastal', seat_count: 5, population: 400000 },
  ]

  const regions: TestWorldData['regions'] = []
  for (const def of regionDefs) {
    const { data: region, error: regionError } = await supabase
      .from('regions')
      .insert({
        nation_id: nation.id,
        name: def.name,
        region_type: def.region_type,
        seat_count: def.seat_count,
        population: def.population,
        economic_profile: { primary_industry: 'services', gdp_per_capita: 30000 },
      })
      .select()
      .single()

    if (regionError) throw new Error(`Failed to create region: ${regionError.message}`)
    regions.push({ id: region.id, name: region.name })
  }

  // Create 2 districts per region (4 total)
  const districtDefs = [
    { regionIdx: 0, name: `Metro District${label}`, settlement_type: 'urban', population: 300000, seat_count: 3 },
    { regionIdx: 0, name: `Suburban District${label}`, settlement_type: 'suburban', population: 200000, seat_count: 2 },
    { regionIdx: 1, name: `Coastal Town${label}`, settlement_type: 'suburban', population: 250000, seat_count: 3 },
    { regionIdx: 1, name: `Rural Coast${label}`, settlement_type: 'rural', population: 150000, seat_count: 2 },
  ]

  const districts: TestWorldData['districts'] = []
  for (const def of districtDefs) {
    const { data: district, error: districtError } = await supabase
      .from('districts')
      .insert({
        region_id: regions[def.regionIdx].id,
        name: def.name,
        settlement_type: def.settlement_type,
        population: def.population,
        seat_count: def.seat_count,
      })
      .select()
      .single()

    if (districtError) throw new Error(`Failed to create district: ${districtError.message}`)
    districts.push({
      id: district.id,
      name: district.name,
      regionId: regions[def.regionIdx].id,
      population: def.population,
    })
  }

  return {
    worldId: world.id,
    nationId: nation.id,
    regions,
    districts,
  }
}

// ============================================================================
// Cohort Generation
// ============================================================================

/**
 * Generates cohorts for all districts in the test world
 */
export async function createTestCohorts(worldData: TestWorldData): Promise<number> {
  let totalCohorts = 0

  // Look up full district data including region type
  const settlementTypes: Record<string, string> = {}
  const regionTypes: Record<string, string> = {}

  for (const district of worldData.districts) {
    // Determine settlement type from name
    if (district.name.includes('Metro')) {
      settlementTypes[district.id] = 'urban'
      regionTypes[district.id] = 'urban_core'
    } else if (district.name.includes('Suburban')) {
      settlementTypes[district.id] = 'suburban'
      regionTypes[district.id] = 'urban_core'
    } else if (district.name.includes('Coastal Town')) {
      settlementTypes[district.id] = 'suburban'
      regionTypes[district.id] = 'coastal'
    } else {
      settlementTypes[district.id] = 'rural'
      regionTypes[district.id] = 'coastal'
    }
  }

  for (const district of worldData.districts) {
    const cohorts = generateCohortsForDistrict({
      districtId: district.id,
      districtPopulation: district.population,
      settlementType: settlementTypes[district.id] as 'urban' | 'suburban' | 'rural',
      regionType: regionTypes[district.id] as 'coastal' | 'mountain' | 'plains' | 'forest' | 'urban_core',
    })

    if (cohorts.length > 0) {
      const { error } = await supabase.from('cohorts').insert(cohorts)
      if (error) throw new Error(`Failed to insert cohorts: ${error.message}`)
      totalCohorts += cohorts.length
    }
  }

  return totalCohorts
}

// ============================================================================
// Party Creation
// ============================================================================

/**
 * Creates test parties with varied ideologies.
 * Uses the admin_create_test_party RPC function to bypass RLS restrictions
 * that prevent multiple parties per user in the same nation.
 */
export async function createTestParties(
  nationId: string,
  count: number = 4
): Promise<TestPartyData[]> {
  const presetKeys: PartyPresetKey[] = [
    'leftProgressive',
    'rightConservative',
    'centrist',
    'greenNiche',
    'farRight',
    'socialist',
  ]

  const partiesToCreate = Math.min(count, presetKeys.length)
  const parties: TestPartyData[] = []

  // Get current user for party creation
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Must be authenticated to create parties')

  for (let i = 0; i < partiesToCreate; i++) {
    const preset = PARTY_PRESETS[presetKeys[i]]

    // Use RPC function which is SECURITY DEFINER — bypasses RLS
    const { data: partyId, error } = await supabase.rpc('admin_create_test_party', {
      p_nation_id: nationId,
      p_user_id: user.id,
      p_name: preset.name,
      p_abbreviation: preset.abbreviation,
      p_color: preset.color,
      p_slogan: `Testing slogan for ${preset.name}`,
      p_ideology_vector: preset.ideology as unknown as number[],
      p_money: 10000,
      p_action_points: 10,
      p_prestige: 50,
      p_momentum: 0,
    })

    if (error) throw new Error(`Failed to create party ${preset.name}: ${error.message}`)

    parties.push({
      id: partyId,
      name: preset.name,
      abbreviation: preset.abbreviation,
      color: preset.color,
      ideology: preset.ideology as IdeologyVector,
    })
  }

  return parties
}

// ============================================================================
// Party State Initialization
// ============================================================================

/**
 * Initialize party_district_states and cohort_party_loyalties for all parties
 */
export async function initializePartyStates(
  nationId: string,
  parties: TestPartyData[]
): Promise<void> {
  // Fetch all districts for this nation
  const { data: districts, error: districtError } = await supabase
    .from('districts')
    .select('id, region:regions!inner(nation_id)')
    .eq('region.nation_id', nationId)

  if (districtError) throw new Error(`Failed to fetch districts: ${districtError.message}`)
  if (!districts || districts.length === 0) return

  // Fetch all cohorts for this nation
  const { data: cohorts, error: cohortError } = await supabase
    .from('cohorts')
    .select('id, district:districts!inner(region:regions!inner(nation_id))')
    .eq('district.region.nation_id', nationId)

  if (cohortError) throw new Error(`Failed to fetch cohorts: ${cohortError.message}`)

  for (const party of parties) {
    // Initialize party_district_states
    const states = districts.map((d) => ({
      party_id: party.id,
      district_id: d.id,
      recognition: 5 + Math.floor(Math.random() * 20), // 5-25 starting recognition
      organization_strength: Math.floor(Math.random() * 10), // 0-10 starting org
    }))

    const { error: stateError } = await supabase
      .from('party_district_states')
      .upsert(states, { onConflict: 'party_id,district_id' })

    if (stateError) throw new Error(`Failed to init district states for ${party.name}: ${stateError.message}`)

    // Initialize cohort_party_loyalties
    if (cohorts && cohorts.length > 0) {
      const loyalties = cohorts.map((c) => ({
        cohort_id: c.id,
        party_id: party.id,
        loyalty_score: Math.floor(Math.random() * 15), // 0-15 starting loyalty
      }))

      const { error: loyaltyError } = await supabase
        .from('cohort_party_loyalties')
        .upsert(loyalties, { onConflict: 'cohort_id,party_id' })

      if (loyaltyError) throw new Error(`Failed to init loyalties for ${party.name}: ${loyaltyError.message}`)
    }
  }
}

// ============================================================================
// Candidate Creation
// ============================================================================

/**
 * Creates 3-5 candidates per party with party leaders and regional candidates.
 */
export async function createTestCandidates(
  parties: TestPartyData[],
  districts: TestWorldData['districts']
): Promise<TestCandidateData[]> {
  const allCandidates: TestCandidateData[] = []
  const sources: RecruitmentSource[] = [
    'youth_wing', 'union', 'business', 'activist', 'think_tank', 'independent',
  ]

  for (const party of parties) {
    const candidateCount = 3 + Math.floor(Math.random() * 3) // 3-5 candidates
    const existingNames: string[] = []

    for (let i = 0; i < candidateCount; i++) {
      const source = sources[i % sources.length]
      const generated = generateCandidate({
        partyId: party.id,
        partyIdeology: party.ideology,
        recruitmentSource: source,
        currentTick: 0,
        existingNames,
      })

      existingNames.push(generated.name)

      // Assign roles
      let role = 'none'
      let assignedDistrictId: string | null = null

      if (i === 0) {
        role = 'party_leader'
      } else if (i <= districts.length && i <= 2) {
        role = 'regional_candidate'
        assignedDistrictId = districts[(i - 1) % districts.length].id
      }

      const { data: candidate, error } = await supabase
        .from('candidates')
        .insert({
          ...generated,
          role,
          assigned_district_id: assignedDistrictId,
        })
        .select()
        .single()

      if (error) throw new Error(`Failed to create candidate: ${error.message}`)

      allCandidates.push({
        id: candidate.id,
        name: candidate.name,
        partyId: party.id,
        role,
      })
    }
  }

  return allCandidates
}

// ============================================================================
// Election Initialization
// ============================================================================

/**
 * Creates a scheduled election with proper campaign timing.
 */
export async function initializeTestElection(
  nationId: string,
  scheduledTick: number = 16,
  campaignLength: number = 4
): Promise<string> {
  const campaignStartTick = Math.max(0, scheduledTick - campaignLength)

  const { data: election, error } = await supabase
    .from('elections')
    .insert({
      nation_id: nationId,
      election_type: 'legislative',
      scheduled_tick: scheduledTick,
      campaign_start_tick: campaignStartTick,
      status: 'scheduled',
      is_early: false,
    })
    .select()
    .single()

  if (error) throw new Error(`Failed to create election: ${error.message}`)

  return election.id
}

// ============================================================================
// Action Seeding
// ============================================================================

/**
 * Seeds varied actions for each party for a given tick.
 * Returns the number of actions created.
 */
export async function seedTestActions(
  parties: TestPartyData[],
  districts: TestWorldData['districts'],
  tick: number,
  actionMix?: 'standard' | 'campaign_heavy' | 'varied'
): Promise<number> {
  let count = 0
  const mix = actionMix ?? 'standard'

  for (let pi = 0; pi < parties.length; pi++) {
    const party = parties[pi]
    const actions = getActionMix(party, parties, districts, tick, pi, mix)

    for (const action of actions) {
      const { error } = await supabase
        .from('party_actions')
        .insert(action)

      if (error) throw new Error(`Failed to create action: ${error.message}`)
      count++
    }
  }

  return count
}

function getActionMix(
  party: TestPartyData,
  allParties: TestPartyData[],
  districts: TestWorldData['districts'],
  tick: number,
  partyIndex: number,
  mix: string
) {
  const baseAction = {
    party_id: party.id,
    tick,
    status: 'queued' as const,
  }

  const targetDistrict = districts[partyIndex % districts.length]
  const otherParty = allParties[(partyIndex + 1) % allParties.length]

  if (mix === 'campaign_heavy') {
    return [
      {
        ...baseAction,
        action_type: 'campaign' as ActionType,
        target_type: 'district' as TargetType,
        target_id: targetDistrict.id,
        action_points_spent: 3,
        parameters: { intensity: 3, message_type: 'economic' },
      },
      {
        ...baseAction,
        action_type: 'attack' as ActionType,
        target_type: 'party' as TargetType,
        target_id: otherParty.id,
        action_points_spent: 3,
        parameters: { intensity: 3, attack_type: 'negative_ads' },
      },
    ]
  }

  // Standard or varied mix: rotate through action types based on party index and tick
  const actionTypes: Array<{
    type: ActionType
    targetType: TargetType
    targetId: string | null
    ap: number
    params: Record<string, unknown>
  }> = [
    {
      type: 'fundraise',
      targetType: 'national',
      targetId: null,
      ap: 2,
      params: { donor_bloc: 'business', intensity: 2 },
    },
    {
      type: 'organize',
      targetType: 'district',
      targetId: targetDistrict.id,
      ap: 2,
      params: { intensity: 2 },
    },
    {
      type: 'internal',
      targetType: 'national',
      targetId: null,
      ap: 1,
      params: { focus: 'loyalty_maintenance' },
    },
    {
      type: 'campaign',
      targetType: 'district',
      targetId: targetDistrict.id,
      ap: 3,
      params: { intensity: 3, message_type: 'economic' },
    },
    {
      type: 'pr',
      targetType: 'national',
      targetId: null,
      ap: 2,
      params: { intensity: 2, pr_type: 'damage_control' },
    },
    {
      type: 'attack',
      targetType: 'party',
      targetId: otherParty.id,
      ap: 3,
      params: { intensity: 3, attack_type: 'negative_ads' },
    },
  ]

  // Pick 2 actions per tick based on rotation
  const idx1 = (tick + partyIndex) % actionTypes.length
  const idx2 = (tick + partyIndex + 3) % actionTypes.length
  const selected = [actionTypes[idx1]]
  if (idx1 !== idx2) selected.push(actionTypes[idx2])

  return selected.map((a) => ({
    ...baseAction,
    action_type: a.type,
    target_type: a.targetType,
    target_id: a.targetId,
    action_points_spent: a.ap,
    parameters: a.params,
  }))
}

// ============================================================================
// Full Test Setup
// ============================================================================

/**
 * Creates a complete test world with all entities.
 * Call this to get a fully ready-to-test game state.
 */
export async function createFullTestSetup(
  partyCount: number = 4,
  suffix: string = ''
): Promise<TestSetupResult> {
  // 1. Create world, nation, regions, districts
  const world = await createTestWorld(suffix)

  // 2. Generate cohorts
  const cohortCount = await createTestCohorts(world)

  // 3. Create parties
  const parties = await createTestParties(world.nationId, partyCount)

  // 4. Initialize party states (district states + cohort loyalties)
  await initializePartyStates(world.nationId, parties)

  // 5. Create candidates
  const candidates = await createTestCandidates(parties, world.districts)

  // 6. Create election
  const electionId = await initializeTestElection(world.nationId)

  return {
    world,
    parties,
    candidates,
    electionId,
    cohortCount,
  }
}

// ============================================================================
// Tick Processor Invocation
// ============================================================================

/**
 * Invokes the tick processor edge function for a world.
 * Returns the response from the edge function.
 */
export async function invokeTick(
  worldId: string,
  force: boolean = false
): Promise<{ success: boolean; data?: unknown; error?: string; duration?: number }> {
  const startTime = Date.now()

  try {
    const { data, error } = await supabase.functions.invoke('process-tick', {
      body: {
        world_id: worldId,
        force,
        trigger_source: 'manual',
      },
    })

    const duration = Date.now() - startTime

    if (error) {
      return { success: false, error: error.message, duration }
    }

    return { success: true, data, duration }
  } catch (err) {
    const duration = Date.now() - startTime
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err),
      duration,
    }
  }
}

// ============================================================================
// Cleanup
// ============================================================================

/**
 * Removes all test world data.
 * Cascading deletes should handle related entities.
 */
export async function cleanupTestWorlds(): Promise<TestCleanupResult> {
  const errors: string[] = []

  // Find test worlds
  const { data: worlds, error: fetchError } = await supabase
    .from('worlds')
    .select('id')
    .like('name', 'Test World%')

  if (fetchError) {
    return { deletedWorlds: 0, errors: [fetchError.message] }
  }

  if (!worlds || worlds.length === 0) {
    return { deletedWorlds: 0, errors: [] }
  }

  let deletedCount = 0

  for (const world of worlds) {
    try {
      // Delete in order respecting FK constraints
      // 1. Get nations
      const { data: nations } = await supabase
        .from('nations')
        .select('id')
        .eq('world_id', world.id)

      if (nations) {
        for (const nation of nations) {
          // Delete election results
          const { data: elections } = await supabase
            .from('elections')
            .select('id')
            .eq('nation_id', nation.id)

          if (elections) {
            for (const election of elections) {
              await supabase.from('election_results').delete().eq('election_id', election.id)
            }
          }

          // Delete governments
          await supabase.from('governments').delete().eq('nation_id', nation.id)

          // Set current_government_id to null before deleting
          await supabase
            .from('nations')
            .update({ current_government_id: null })
            .eq('id', nation.id)

          // Delete elections
          await supabase.from('elections').delete().eq('nation_id', nation.id)

          // Get parties
          const { data: parties } = await supabase
            .from('parties')
            .select('id')
            .eq('nation_id', nation.id)

          if (parties) {
            for (const party of parties) {
              // Delete faction memberships → factions → candidates
              const { data: factions } = await supabase
                .from('factions')
                .select('id')
                .eq('party_id', party.id)

              if (factions) {
                for (const faction of factions) {
                  await supabase.from('faction_memberships').delete().eq('faction_id', faction.id)
                }
                // Null out leader refs before deleting factions
                await supabase
                  .from('factions')
                  .update({ leader_candidate_id: null })
                  .eq('party_id', party.id)
                await supabase.from('factions').delete().eq('party_id', party.id)
              }

              // Delete party actions
              await supabase.from('party_actions').delete().eq('party_id', party.id)

              // Delete cohort party loyalties
              await supabase.from('cohort_party_loyalties').delete().eq('party_id', party.id)

              // Delete party district states
              await supabase.from('party_district_states').delete().eq('party_id', party.id)

              // Delete candidates
              await supabase.from('candidates').delete().eq('party_id', party.id)
            }

            // Delete parties
            for (const party of parties) {
              await supabase.from('parties').delete().eq('id', party.id)
            }
          }

          // Get regions → districts → cohorts
          const { data: regions } = await supabase
            .from('regions')
            .select('id')
            .eq('nation_id', nation.id)

          if (regions) {
            for (const region of regions) {
              const { data: districts } = await supabase
                .from('districts')
                .select('id')
                .eq('region_id', region.id)

              if (districts) {
                for (const district of districts) {
                  await supabase.from('cohorts').delete().eq('district_id', district.id)
                }
                for (const district of districts) {
                  await supabase.from('districts').delete().eq('id', district.id)
                }
              }
            }
            for (const region of regions) {
              await supabase.from('regions').delete().eq('id', region.id)
            }
          }

          // Delete polling snapshots
          await supabase.from('polling_snapshots').delete().eq('nation_id', nation.id)
        }

        // Delete nations
        for (const nation of nations) {
          await supabase.from('nations').delete().eq('id', nation.id)
        }
      }

      // Delete ticks
      await supabase.from('ticks').delete().eq('world_id', world.id)

      // Delete world
      const { error: deleteError } = await supabase.from('worlds').delete().eq('id', world.id)
      if (deleteError) {
        errors.push(`Failed to delete world ${world.id}: ${deleteError.message}`)
      } else {
        deletedCount++
      }
    } catch (err) {
      errors.push(`Error cleaning world ${world.id}: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  return { deletedWorlds: deletedCount, errors }
}

/**
 * Reset party resources (money, AP) for continued testing
 */
export async function resetPartyResources(partyIds: string[]): Promise<void> {
  for (const id of partyIds) {
    const { error } = await supabase
      .from('parties')
      .update({ money: 10000, action_points: 10, momentum: 0 })
      .eq('id', id)

    if (error) throw new Error(`Failed to reset party ${id}: ${error.message}`)
  }
}
